import React, { useRef, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { SERVICES } from "../servicesData";
import SEO from "../components/SEO";
import Nav from "../components/Nav";
import Footer from "../components/Footer";

/* ── tiny scroll-reveal hook ── */
function useReveal(threshold = 0.12) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    if (!ref.current) return;
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) { setVisible(true); obs.disconnect(); } },
      { threshold }
    );
    obs.observe(ref.current);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, visible];
}

/* ── Animated counter ── */
function Counter({ to, suffix = "" }) {
  const [val, setVal] = useState(0);
  const [ref, visible] = useReveal(0.5);
  useEffect(() => {
    if (!visible) return;
    let start = 0;
    const step = Math.ceil(to / 50);
    const t = setInterval(() => {
      start += step;
      if (start >= to) { setVal(to); clearInterval(t); }
      else setVal(start);
    }, 20);
    return () => clearInterval(t);
  }, [visible, to]);
  return <span ref={ref}>{val}{suffix}</span>;
}

/* ── Service card ── */
function ServiceCard({ service, index }) {
  const [ref, visible] = useReveal();
  const [hovered, setHovered] = useState(false);

  return (
    <div
      ref={ref}
      className={"sp-card" + (visible ? " sp-card--visible" : "")}
      style={{ transitionDelay: `${(index % 3) * 0.08}s` }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {/* Background image on hover */}
      <div
        className="sp-card-bg"
        style={{ backgroundImage: `url(${service.hero})`, opacity: hovered ? 0.18 : 0 }}
      />

      <div className="sp-card-inner">
        <div className="sp-card-top">
          <span className="sp-card-num">{service.num}</span>
          <span className="sp-card-tag">{service.items.length} services</span>
        </div>

        <h3 className="sp-card-title">{service.title}</h3>
        <p className="sp-card-tagline">{service.tagline}</p>

        <ul className="sp-card-items">
          {service.items.slice(0, 5).map((item) => (
            <li key={item}>{item}</li>
          ))}
          {service.items.length > 5 && (
            <li className="sp-card-more">+{service.items.length - 5} more</li>
          )}
        </ul>

        <Link to={`/services/${service.id}`} className="sp-card-cta">
          <span>Read More</span>
          <svg width="14" height="10" viewBox="0 0 14 10" fill="none">
            <path d="M8 1L13 5L8 9" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M13 5H1" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
          </svg>
        </Link>
      </div>
    </div>
  );
}

export default function ServicesPage() {
  const [heroRef, heroVisible] = useReveal(0.01);
  const [introRef, introVisible] = useReveal(0.2);

  /* scroll to top on mount */
  useEffect(() => { window.scrollTo(0, 0); }, []);

  const servicesSchema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "@id": "https://ovarddigital.com/services#webpage",
    "url": "https://ovarddigital.com/services",
    "name": "Digital Marketing Services | OVARD Digital",
    "description": "OVARD Digital offers 13 categories of digital marketing services — brand identity, websites, e-commerce, content creation, social media, Meta Ads, Google Ads, SEO, automation and AI systems.",
    "isPartOf": { "@id": "https://ovarddigital.com/#website" },
    "breadcrumb": {
      "@type": "BreadcrumbList",
      "itemListElement": [
        { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://ovarddigital.com/" },
        { "@type": "ListItem", "position": 2, "name": "Services", "item": "https://ovarddigital.com/services" }
      ]
    }
  };

  return (
    <div className="sp-page">

      <SEO
        title="Digital Marketing Services"
        description="OVARD Digital offers 13 categories of digital marketing services — brand identity, websites, e-commerce, content creation, social media, Meta Ads, Google Ads, SEO, automation and AI systems for businesses in India."
        canonical="/services"
        schema={servicesSchema}
        breadcrumbs={[
          { name: "Home", path: "/" },
          { name: "Services", path: "/services" }
        ]}
      />

      <Nav />

      {/* ── Hero ── */}
      <section className={"sp-hero" + (heroVisible ? " sp-hero--play" : "")} ref={heroRef}>
        <div className="sp-hero-grain" aria-hidden="true" />
        <div className="sp-hero-glow" aria-hidden="true" />
        <div className="sp-hero-glow sp-hero-glow-2" aria-hidden="true" />

        <div className="container sp-hero-content">
          <div className="sp-hero-eyebrow">
            <span className="sp-hero-dot" />
            OVARD DIGITAL — WHAT WE DO
          </div>
          <h1 className="sp-hero-h1">
            <span className="sp-hero-line"><span>Every service</span></span>
            <span className="sp-hero-line sp-hero-line--indent"><span>your business</span></span>
            <span className="sp-hero-line sp-hero-line--gold"><span>needs to grow.</span></span>
          </h1>
          <p className="sp-hero-sub">
            13 specialist categories. One integrated agency. From brand identity to AI systems — everything built to work together.
          </p>
        </div>

        {/* Stat strip */}
        <div className="sp-hero-stats">
          <div className="container sp-hero-stats-inner">
            {[
              { val: 13, suffix: "", label: "Service Categories" },
              { val: 160, suffix: "+", label: "Individual Services" },
              { val: 100, suffix: "%", label: "Client Focused" },
              { val: 1, suffix: "", label: "Goal: Your Growth" },
            ].map((s) => (
              <div className="sp-stat" key={s.label}>
                <span className="sp-stat-num">
                  <Counter to={s.val} suffix={s.suffix} />
                </span>
                <span className="sp-stat-label">{s.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Intro strip ── */}
      <div
        className={"sp-intro" + (introVisible ? " sp-intro--visible" : "")}
        ref={introRef}
      >
        <div className="container sp-intro-inner">
          <p className="sp-intro-text">
            We don't sell packages. We build what your business actually needs — whether that's one service or the full system.
          </p>
          <a href="#services-grid" className="sp-intro-scroll">
            Explore services
            <span className="sp-intro-line" />
          </a>
        </div>
      </div>

      {/* ── Services Grid ── */}
      <section id="services-grid" className="sp-grid-section">
        <div className="container">
          <div className="sp-grid">
            {SERVICES.map((service, i) => (
              <ServiceCard key={service.id} service={service} index={i} />
            ))}
          </div>
        </div>
      </section>

      {/* ── Bottom CTA ── */}
      <BottomCTA />
      <Footer />

    </div>
  );
}

function BottomCTA() {
  const [ref, visible] = useReveal(0.3);
  return (
    <section
      className={"sp-cta" + (visible ? " sp-cta--visible" : "")}
      ref={ref}
    >
      <div className="sp-cta-grain" aria-hidden="true" />
      <div className="container sp-cta-inner">
        <span className="sp-cta-eyebrow">Ready to start?</span>
        <h2 className="sp-cta-h2">
          Not sure which services you need?<br />
          <span>Let's figure it out together.</span>
        </h2>
        <p className="sp-cta-sub">
          Most of our clients come in knowing they need something — not exactly what. That's fine. We'll ask the right questions and build the right plan.
        </p>
        <Link to="/#contact" className="sp-cta-btn">
          <span>Start a conversation</span>
          <svg width="15" height="11" viewBox="0 0 15 11" fill="none">
            <path d="M9 1L14 5.5L9 10" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M14 5.5H1" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
          </svg>
        </Link>
      </div>
    </section>
  );
}
